import { GuestFilters, GuestsResponse } from "@/types/guest";
import { ApiError, BaseService } from "./BaseService";
import { IHotelService } from "./interfaces/IHotelService";
import { HotelServicesResponse } from "@/types/hotelService";
import { SettlementFilters } from "@/components/pages/settlement/hooks/useSettlement";
import { SettlementBooking } from "@/components/pages/settlement/types/settlementBooking";
import { Hotel } from "@/types/hotel";
import {
  Hotel as HotelBrand,
  CreateHotelDto,
  UpdateHotelDto,
  HotelFilters,
  IPaginationResponse,
  HotelsListResponse,
  ProfileHotelAccess,
} from "@/types/hotel";
import { getSelectedHotelId } from "@/utils/hotelContext";
import type {
  SettlementBookingType,
  SettlementSummaryMap,
} from "@/components/pages/settlement/types/settlementBookingSummary";

function isPaginatedHotelsResponse(
  value: HotelBrand[] | IPaginationResponse<HotelBrand>,
): value is IPaginationResponse<HotelBrand> {
  return (
    !Array.isArray(value) &&
    value !== null &&
    typeof value === "object" &&
    "data" in value &&
    "total" in value
  );
}

const getHotelEnabledState = (
  hotel?: Pick<HotelBrand, "isEnabled" | "isActive">,
): boolean | undefined => hotel?.isEnabled ?? hotel?.isActive;

class HotelService extends BaseService implements IHotelService {
  private basePath = "/hotels";
  private adminBasePath = "/admin/hotel";

  constructor() {
    super();
  }

  async getGuestsWithLastBooking(
    hotelId: string,
    filters?: GuestFilters,
  ): Promise<GuestsResponse> {
    const dateRange = filters?.dateRange;

    delete filters?.dateRange;

    return this.get<GuestsResponse>(`${this.basePath}/${hotelId}/guests`, {
      ...filters,
      ...dateRange,
    });
  }

  async getNationalities(hotelId?: string): Promise<string[]> {
    return this.get<string[]>(
      `${this.basePath}/${hotelId}/guests/nationalities`,
    );
  }

  async getServices(
    hotelId: string,
    page: number = 1,
    limit: number = 50,
  ): Promise<HotelServicesResponse> {
    return this.get<HotelServicesResponse>(
      `${this.basePath}/${hotelId}/services`,
      { page, limit },
    );
  }
  async getSettlement(
    filters: SettlementFilters,
    hotelId?: string,
  ): Promise<{ total: number; data: SettlementBooking[] }> {
    return this.get<{ total: number; data: SettlementBooking[] }>(
      `${this.basePath}/${hotelId}/settlement`,
      filters,
    );
  }

  async getSettlementSummary<T extends SettlementBookingType>(
    type: T,
    hotelId?: string,
  ): Promise<SettlementSummaryMap[T] | null> {
    try {
      return await this.get<SettlementSummaryMap[T]>(
        `${this.basePath}/${hotelId}/settlement/summary`,
        { type },
        {
          // Settlement summary is optional in some environments.
          // Keep the page usable when the backend does not expose this route.
          disableToast: true,
        },
      );
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 404) {
        return null;
      }

      throw error;
    }
  }

  async getUniqueBookingSources(hotelId?: string): Promise<string[]> {
    return this.get<string[]>(`${this.basePath}/${hotelId}/booking-sources`);
  }

  getUserHotel(hotelId?: string): Promise<Hotel | null> {
    // Keep the request tied to the query key when switching hotels. Reading
    // localStorage inside the request can otherwise make an in-flight query
    // for the previous hotel fetch the newly selected hotel instead.
    const selectedHotelId = hotelId?.trim() || getSelectedHotelId();

    return this.get<Hotel | null>(`${this.basePath}/user-hotel`, undefined, {
      headers: selectedHotelId ? { "x-hotel-id": selectedHotelId } : undefined,
    });
  }

  async getProfileHotels(options?: {
    includeHotelDetailsFallback?: boolean;
  }): Promise<ProfileHotelAccess[]> {
    const profileHotels = await this.get<ProfileHotelAccess[]>(
      "/profile/hotels",
      undefined,
      {
        skipSelectedHotelHeader: true,
      },
    );

    const hasEnabledState = profileHotels.every(
      (hotel) => hotel.isEnabled !== undefined || hotel.isActive !== undefined,
    );

    if (hasEnabledState) {
      return profileHotels;
    }

    if (options?.includeHotelDetailsFallback === false) {
      return profileHotels.map((profileHotel) => ({
        ...profileHotel,
        isEnabled: profileHotel.isEnabled ?? profileHotel.isActive ?? true,
      }));
    }

    let hotelsById = new Map<string, HotelBrand>();

    try {
      const rawHotels = await this.get<
        HotelBrand[] | IPaginationResponse<HotelBrand>
      >(
        this.basePath,
        { perPage: 1000 },
        {
          disableToast: true,
          skipSelectedHotelHeader: true,
        },
      );
      const hotels = isPaginatedHotelsResponse(rawHotels)
        ? rawHotels.data
        : rawHotels;
      hotelsById = new Map(hotels.map((hotel) => [hotel.id, hotel]));
    } catch {
      hotelsById = new Map<string, HotelBrand>();
    }

    return profileHotels.map((profileHotel) => {
      const hotel = hotelsById.get(profileHotel.hotelId);

      return {
        ...profileHotel,
        hotelName: profileHotel.hotelName || hotel?.name || "",
        isEnabled:
          profileHotel.isEnabled ??
          getHotelEnabledState(hotel) ??
          profileHotel.isActive ??
          true,
        isActive: profileHotel.isActive ?? hotel?.isActive,
      };
    });
  }

  async getHotels(filters?: HotelFilters): Promise<HotelsListResponse> {
    const path = filters?.tenantId ? this.adminBasePath : this.basePath;
    const raw = await this.get<HotelBrand[] | HotelsListResponse>(
      path,
      filters,
    );

    if (isPaginatedHotelsResponse(raw)) {
      return raw;
    }

    return {
      data: raw,
      total: raw.length,
      summary: {
        total: raw.length,
        active: raw.filter((hotel) => getHotelEnabledState(hotel) !== false)
          .length,
        inactive: raw.filter((hotel) => getHotelEnabledState(hotel) === false)
          .length,
        totalRooms: raw.reduce(
          (sum, hotel) => sum + (hotel.rooms?.length ?? 0),
          0,
        ),
      },
    };
  }

  async getHotelById(hotelId: string): Promise<HotelBrand> {
    return this.get<HotelBrand>(`${this.basePath}/${hotelId}`);
  }

  async createHotel(hotelData: CreateHotelDto): Promise<HotelBrand> {
    return this.post<HotelBrand>(this.basePath, hotelData);
  }

  async updateHotel(
    hotelId: string,
    hotelData: UpdateHotelDto,
  ): Promise<HotelBrand> {
    return this.put<HotelBrand>(`${this.basePath}/${hotelId}`, hotelData);
  }

  async deleteHotel(hotelId: string): Promise<void> {
    return this.delete<void>(`${this.basePath}/${hotelId}`);
  }

  async activateHotel(hotelId: string): Promise<void> {
    return this.put<void>(`${this.basePath}/${hotelId}`, { isEnabled: true });
  }

  async deactivateHotel(hotelId: string): Promise<void> {
    return this.put<void>(`${this.basePath}/${hotelId}`, { isEnabled: false });
  }
}

export const hotelService = new HotelService();
